declare let __BUILD_VERSION__: string;

import { Inject, Injectable } from "@angular/core";

import { ServiceMessageBrokerFactory, PRIMITIVE } from "shared/provider-webworker";

import { Observable, Subject, TimeoutError } from "rxjs";
import { ConnectionStatus, ConnectionStatusState } from "shared/broker-client-services";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  CubiqBrokerClient,
  CUBIQ_BROKER,
  CUBIQ_BROKER_PORT,
  CUBIQ_LOGIN_CLOSE_AFTER_TOKEN,
  MISC_STATIC_DATA,
} from "./CubiqBrokerClient";

import { PromiseSet } from "../promise/PromiseSet";
import { PrimaryPromiseSet } from "../promise/PrimaryPromiseSet";
import { LogonResp } from "../model/core/LogonResp";
import { SessionStoreData } from "../model/core/SessionStoreData";

import { RequestEncodable } from "./RequestEncodable";

export const CUBIQ_AUTH_CHANNEL = "CUBIQ_AUTH";
export const CRED_RESET = "auth.cred.reset";
export const CRED_EXP = "auth.credential.expired";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TRADING_SOURCE_INTERNAL } from "../model/TradingSource.constants";
import { TokenState } from "shared/broker-client-services";
import { filter, timeout } from "rxjs/operators";
import { User } from "src/app/bfx-common/models/user.model";
import { CubiqLogger } from "shared/broker-client-services/worker/CubiqLogger";

const MASK_BMFIXINGS = 0x10000;
const MASK_HEDGE_DEFAULTS = 0x20000;
const MASK_PLATFORM_PROFILES = 0x80000;
const PROPERTY_TRADING_SOURCE: string = "tradingChannel";
const TIMEOUT_ERROR: any[] = ['{ "id": "auth.login.timed.out" }'];

export const SKIP_PRIME = Symbol("SKIP_PRIME");

@Injectable()
export class CubiqWorker {
  private secureToken = null;
  private secureTokenResponse = null;

  private tokenSubject: Subject<SessionStoreData | null> = new Subject();

  public tokenEvent$ = this.tokenSubject.asObservable();

  private _ready: PromiseSet<void> = new PrimaryPromiseSet<void>();
  private _auth: PromiseSet<void> = new PrimaryPromiseSet<void>();
  private _isDataProfilePrimed = {};
  private _serializer = new CubiqTS.CubiqSerializer();
  private _locationURI: string = null;

  private isResetRequested: boolean = false;
  private resetUserCd: string = null;

  private config;

  private cubiqLogger: CubiqLogger;

  private get serviceResponseSubject() {
    return this.broker.getEncodableObservable<CubiqTS.ServiceResponseImpl>(
      CubiqTS.ServiceResponseImpl.name,
    );
  }

  constructor(
    private _serviceBrokerFactory: ServiceMessageBrokerFactory,
    @Inject(CUBIQ_BROKER) private broker: CubiqBrokerClient,
    @Inject(CUBIQ_BROKER_PORT) port: MessagePort,
    @Inject(CUBIQ_LOGIN_CLOSE_AFTER_TOKEN) private closeAfterToken: boolean,
  ) {
    if (this.broker.hasSingleton()) {
      // this acts like a backport of providedIn: 'platform'
      return this.broker.getSingleton() as CubiqWorker;
    } else {
      this.broker.setSingleton(this);
    }
    this.cubiqLogger = this.broker.getCubiqLogger();
    this.broker.setSourcePort(port);

    const serviceBroker = _serviceBrokerFactory.createMessageBroker(CUBIQ_AUTH_CHANNEL, true, true);
    serviceBroker.registerMethod("isAuth", [PRIMITIVE], this.isAuth.bind(this), PRIMITIVE);

    // FIXME: Replace with Stream pattern
    serviceBroker.registerMethod(
      "registerEventChannelListener",
      [PRIMITIVE, PRIMITIVE],
      (typename: string, port: MessagePort) => {
        return new Promise<void>((res, _) => {
          // typename must be ConcreteEncodable
          let t: any = CubiqTS[typename];
          let VALUE = CubiqTS.ENCODABLE(t);

          this.broker.registerEventChannelListener(
            typename,
            (encodable: CubiqTS.ConcreteEncodable) => {
              let buffer: ArrayBuffer = this._serializer.serialize(encodable, VALUE) as ArrayBuffer;

              if (buffer instanceof ArrayBuffer && buffer.byteLength >= 0) {
                port.postMessage(buffer, [buffer]);
              }
            },
          );

          port.start();

          res();
        });
      },
      PRIMITIVE,
    );

    this.broker.registerEventChannelListener(CubiqTS.ServiceResponseImpl.name);

    this.broker.registerEventChannelListener(
      CubiqTS.StaticDataProfileRequestImpl.name,
      (event: CubiqTS.StaticDataProfileRequestImpl) => {
        logger.out.info(CubiqTS.StaticDataProfileRequestImpl.name + " event: " + event);
      },
    );

    this.broker.registerEventChannelListener(CubiqTS.ModelUpdateImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.AdminModelUpdateImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.OrderLifeCycleEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.DealImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.InterventionEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.OpsIntEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.SysCtrlEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.LockLifeCycleEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.HeartBeatImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.XferEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.BulkOrderLifeCycleEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.TrailingStopEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.SystemMessageImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.TradingCalendarSnapShotImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.PauseLifeCycleEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.UnsolicitedCancelLifeCycleEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.SavedOrderEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.SavedOrderEventListImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.FixingEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.BenchmarkLockLifeCycleEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.BenchmarkFixingImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.FixingOrderUpdateEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.SystemMonitorEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.ApplicationMessageEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.ChangeHedgingModeEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.AutoHedgingStatusEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.BenchmarkPositionAuditImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.ManualNettingBulkEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.ManualNettingEventImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.PingTimerImpl.name);
    this.broker.registerEventChannelListener(CubiqTS.AdjustedTenorForHolidayEventImpl.name);

    // Subscribe to Gamma events and simple unwrap the inner OrderLifecycleEvents and feed them back
    this.broker.registerEventChannelListener(
      CubiqTS.GammaOrderLifeCycleEventImpl.name,
      (gammaEvent: CubiqTS.GammaOrderLifeCycleEventImpl) => {
        const events = gammaEvent.eventList;
        if (events) {
          events.forEach((event) =>
            this.broker.injectEncodable(event, CubiqTS.OrderLifeCycleEventImpl.name),
          );
        }
      },
      true, // Set to false to disable creation of a Subject listener which can e subscribed by other clients
    );

    this.loadConfiguration().then(
      (config) => {
        this.config = config;

        // reconnectionInterval is now handled as a setTimeout reconnectionDelay
        this.broker.reconnectionDelay =
          config.reconnectionInterval >= 500 ? config.reconnectionInterval : 500;

        if (this.config.initialConnectionDelay != null && this.config.initialConnectionDelay > 0) {
          setTimeout(() => {
            this.connect();
          }, this.config.initialConnectionDelay);
        } else {
          this.connect();
        }
      },
      (err) => {
        logger.out.error("ERROR: CubiqService loadConfiguration()", err);
        this.connect();
      },
    );

    let readySubscription = this.broker.connectionEvent$.subscribe((connectionStatus) => {
      if (connectionStatus.getStatus() == ConnectionStatusState.CONNECTED) {
        this._ready.resolve();
        readySubscription.unsubscribe();
      }
    });
  }

  public connect(): void {
    if (this.broker.getConnectionStatus().getStatus() == ConnectionStatusState.CONNECTED) {
      logger.out.warn(`${this.constructor.name}.connect() already connected`);
      return;
    }

    let protocol = this.config.protocol || `${self.location.protocol}//`;
    let hostname = this.config.hostname || self.location.hostname;
    let port = this.config.port || self.location.port;
    this._locationURI = `${protocol}${hostname}:${port}`;
    this.broker.connect(this._locationURI);
    logger.out.info(`${this.constructor.name}.connect() ${this._locationURI}`);
    if (this.broker.getConnectionStatus().getStatus() == ConnectionStatusState.CONNECTED) {
      fetch("/assets/JSON/version.json")
        .then((r) => r.json())
        .then((v) => {
          if (v.version !== __BUILD_VERSION__) {
            location.reload();
          }
        });
    }
  }

  public getBroker(): CubiqBrokerClient {
    return this.broker;
  }

  private loadConfiguration(): Promise<any> {
    return new Promise<any>((res, rej) => {
      let xhr = new XMLHttpRequest();
      xhr.open("GET", module?.hot ? "/__webpack/proxy_target" : "/assets/JSON/cubiq.json", true);
      xhr.onload = function (e) {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            // logger.out.info(xhr.responseText);
            let config = JSON.parse(xhr.responseText);
            res(config);
          } else {
            rej(xhr.statusText);
          }
        }
      };
      xhr.onerror = function (e) {
        rej(xhr.statusText);
      };
      xhr.send(null);
    });
  }

  /**
   * aggregates multiple- differing async StaticDataProfileRequestImpls
   * the first specified profile returned from server will be checked for ui user perms
   * (suggested mask 0, type CubiqBrokerClient.name)
   * if these do not exist Observable will return an error for display on login. -dc
   */
  public processLogin(): Observable<void> {
    let subject = new Subject<void>(); // early failure mode for uiEnabledFg
    let initialMessageId: string;
    let msgIds: Set<string> = new Set();
    let msgIdtoTypeMap: Map<string, string[]> = new Map();
    let requests: Array<CubiqTS.StaticDataProfileRequestImpl> = [];
    let masks: Array<{ mask: CubiqTS.Long; types: string[] }> = [
      { mask: new CubiqTS.Long(0), types: [CubiqBrokerClient.name] },
      ...(this.closeAfterToken
        ? []
        : [
            { mask: new CubiqTS.Long(1), types: [CubiqTS.ClientImpl.name] },
            { mask: new CubiqTS.Long(2), types: [CubiqTS.AccountImpl.name] },
            { mask: new CubiqTS.Long(8), types: [CubiqTS.CurrencyImpl.name] },
            {
              mask: new CubiqTS.Long(4),
              types: [CubiqTS.CurrencyPairImpl.name],
            },
            {
              mask: new CubiqTS.Long(
                16 |
                  32 |
                  64 |
                  128 |
                  256 |
                  512 |
                  1024 |
                  2048 |
                  4096 |
                  8192 |
                  16384 |
                  32768 |
                  MASK_BMFIXINGS |
                  MASK_HEDGE_DEFAULTS |
                  MASK_PLATFORM_PROFILES,
              ),
              types: [MISC_STATIC_DATA],
            },
          ]),
    ];
    masks.forEach((mask, i) => {
      let staticRequest: CubiqTS.StaticDataProfileRequestImpl =
        new CubiqTS.StaticDataProfileRequestImpl();
      msgIds.add(staticRequest.messageId);
      if (i === 0) {
        initialMessageId = staticRequest.messageId;
      }
      msgIdtoTypeMap.set(staticRequest.messageId, mask.types);
      staticRequest.secureToken = this.secureToken;
      staticRequest.dataMask = mask.mask;
      staticRequest.sessionOnly = "true";
      requests.push(staticRequest);
    });

    this.broker.processLogin(requests, msgIds, msgIdtoTypeMap).subscribe(
      (response) => {
        logger.out.info(
          `${this.constructor.name} successfully processed Static Data ${response.messageId}`,
        );
        if (initialMessageId === response.messageId && response.errors != null) {
          subject.error(response.errors);
          subject.complete();
        } else if (initialMessageId === response.messageId) {
          subject.complete();
        }
      },
      (error) => {
        this.broker.disconnect();
        this._auth.reject(error);
      },
      () => {
        this._auth.resolve();
      },
    );

    return subject.asObservable();
  }

  public requestLogin(
    user: any,
    timeoutInterval?: number,
    sso: boolean = false,
  ): Promise<LogonResp> {
    let logonRequest: CubiqTS.LogonRequestImpl = new CubiqTS.LogonRequestImpl();
    let secTk = sso ? new CubiqTS.SSOToken() : new CubiqTS.BaseUserToken();
    let brokerTokenSubscription;
    let brokerTokenState = new Promise<TokenState>((resolve) => {
      // token state is irrelvent here, just that request has been ack'd
      brokerTokenSubscription = this.broker.token$.subscribe(resolve);
    });

    logonRequest.userCode = user.username;
    logonRequest.passWd = user.password;

    if (sso && secTk instanceof CubiqTS.SSOToken) {
      secTk.token = user.password;
    } else if (secTk instanceof CubiqTS.BaseUserToken) {
      secTk.userName = user.username;
      secTk.password = user.password;
    }

    secTk.timeStamp = new CubiqTS.Long(new Date().getTime());
    logonRequest.secureToken = secTk;

    this.broker.requestTokenId();

    // Required for session identification
    logonRequest.extendedInfo.set(PROPERTY_TRADING_SOURCE, TRADING_SOURCE_INTERNAL);

    return new Promise<LogonResp>((resolve, reject) => {
      logger.out.info(
        `CubiqWorker: this.isReady() guard _ready:%O _auth:%O`,
        this._ready,
        this._auth,
      );

      if (this._auth.isResolved() && this.secureToken === null) {
        logger.out.info(
          `CubiqWorker: requestLogin this.isReady() early resolve: auth:%O secureToken isnull:%O`,
          this._auth,
          this.secureToken === null,
        );
        reject(new Error("Login already in progress"));
      } else if (this.secureToken !== null) {
        logger.out.info(
          `CubiqWorker: requestLogin this.isReady() early resolve: not auth:%O secureToken isnull:%O`,
          this._auth,
          this.secureToken === null,
        );
        reject(new Error("Login already completed"));
      }

      Promise.all([this.isReady(), brokerTokenState]).then(
        () => {
          brokerTokenSubscription.unsubscribe();
          let logonObservable: Observable<CubiqTS.ServiceResponseImpl> = null;
          let filterOp = filter((serviceResponse: CubiqTS.ServiceResponseImpl) => {
            return serviceResponse.messageId === logonRequest.messageId;
          });

          if (timeoutInterval != null && timeoutInterval > 0) {
            logger.out.info(
              `${this.constructor.name}: Logon request timeout specified at ${timeoutInterval}ms`,
            );
            logonObservable = this.serviceResponseSubject.pipe(filterOp, timeout(timeoutInterval));
          } else {
            logger.out.info(
              `${this.constructor.name}: Logon request timeout not specified or less or equal to 0.`,
            );
            logonObservable = this.serviceResponseSubject.pipe(filterOp);
          }

          let subscription = logonObservable.subscribe({
            next: (serviceResponse: CubiqTS.ServiceResponseImpl) => {
              let responseCode: any = serviceResponse.responseCode;
              if (
                CubiqTS.ResponseCode.COMPLETE === responseCode &&
                serviceResponse.payload != null &&
                serviceResponse.payload instanceof CubiqTS.TrustToken
              ) {
                let hasErrors = false;

                this.secureTokenResponse = serviceResponse;
                this.secureToken = serviceResponse.payload;
                this.broker.setToken(serviceResponse);

                this.processLogin().subscribe(
                  () => {
                    // valid request - no-op
                  },
                  (errors: CubiqTS.ErrorsImpl) => {
                    // first error will display on login screen
                    hasErrors = true;
                    let resp = new LogonResp(false, errors);
                    subscription.unsubscribe();
                    resolve(resp);
                  },
                  () => {
                    if (!hasErrors) {
                      this.tokenSubject.next(this.processToken(this.secureToken));
                      logger.out.info(`CubiqWorker: requestLogin resolve true`);
                      subscription.unsubscribe();
                      resolve(new LogonResp(true));
                    }
                  },
                );
              } else if (
                CubiqTS.ResponseCode.INVALID_REQUEST === responseCode ||
                CubiqTS.ResponseCode.SERVICE_ERROR === responseCode ||
                CubiqTS.ResponseCode.BROKER_ERROR === responseCode
              ) {
                logger.out.info(`CubiqWorker: requestLogin resolve false`);
                subscription.unsubscribe();
                let resp = new LogonResp(false);
                if (serviceResponse.errorList != null) {
                  resp.errors = serviceResponse.errorList;
                  let errMsgId = JSON.parse(resp.errors.errorMessages[0].toString())["id"];
                  if (errMsgId === CRED_RESET || errMsgId === CRED_EXP) {
                    this.isResetRequested = true;
                    this.secureToken = secTk;
                    this.resetUserCd = user.username;
                  }
                }
                resolve(resp);
              }
            },
            error: (error) => {
              // Check if timeout is specified, just in case
              if (error instanceof TimeoutError && timeoutInterval != null && timeoutInterval > 0) {
                logger.out.error(
                  `${this.constructor.name}: Login request timed out. Disconnecting ...`,
                );
                this.disconnect();

                let errors: CubiqTS.Errors = new CubiqTS.ErrorsImpl();
                errors.errorMessages = TIMEOUT_ERROR;
                let resp = new LogonResp(false, errors);

                subscription.unsubscribe();
                resolve(resp);
              } else {
                logger.out.error(
                  `CubiqWorker: requestLogin() _ready:%O _auth:%O %O`,
                  this._ready,
                  this._auth,
                  error,
                );
                this._auth.reject(error);
                reject(error);
              }
            },
            complete: () => {
              logger.out.info("logonRequest flow completed");
            },
          });

          // updates message id if required
          this.broker.sendLoginEvent(logonRequest);
        },
        (reason) => {
          logger.out.error(
            `CubiqWorker: requestLogin() this.isReady() rejected _ready:%O _auth:%O %O`,
            this._ready,
            this._auth,
            reason,
          );
        },
      );
    });
  }

  public primeDataProfile<T>(
    filterNames: string[],
    typeName: string | symbol,
    extendedInfo: Map<string, string> = null,
  ): Observable<T[]> {
    let dataProfileSubject: Subject<T[]> = new Subject();

    let dataProfileRequest =
      typeName === CubiqTS.LinkedOrderRequestImpl.name
        ? new CubiqTS.SavedProfileRequestImpl()
        : new CubiqTS.DataProfileRequestImpl();
    dataProfileRequest.secureToken = this.secureToken;
    dataProfileRequest.profileDescriptors = [];

    if (filterNames.length == 0) {
      if (typeName !== SKIP_PRIME) {
        logger.out.info(
          `WARN: Data profile of type ${typeName.toString()} requested with an empty profile list.`,
        );
      }
      dataProfileSubject.complete();
      return dataProfileSubject.asObservable();
    }

    for (let i: number = 0; i < filterNames.length; i++) {
      let desc = new CubiqTS.DataProfileDescriptorImpl();
      desc.filterName = filterNames[i];
      dataProfileRequest.profileDescriptors.push(desc);
    }

    // added for Bmark recent order data retrieval through profile
    if (extendedInfo instanceof Map) {
      dataProfileRequest.extendedInfo = extendedInfo;
    }

    let dataProfileSubscription = this.serviceResponseSubject
      .pipe(
        filter((serviceResponse: CubiqTS.ServiceResponseImpl) => {
          return serviceResponse.messageId === dataProfileRequest.messageId;
        }),
      )
      .subscribe(
        (serviceResponse: CubiqTS.ServiceResponseImpl) => {
          if (
            serviceResponse != null &&
            serviceResponse.messageId == dataProfileRequest.messageId
          ) {
            let dataProfileResponse = serviceResponse.payload as CubiqTS.DataProfileResponseImpl;

            if (dataProfileResponse != null && Array.isArray(dataProfileResponse.responseData)) {
              if (dataProfileResponse && dataProfileResponse.responseData.length === 0) {
                logger.out.info(
                  "dataProfileResponseImpl empty responseData ",
                  serviceResponse.serviceRequestType,
                );
                dataProfileSubject.next([]);
              }

              for (let i = 0; i < dataProfileResponse.responseData.length; i++) {
                let standardReport: CubiqTS.StandardReportImpl = dataProfileResponse.responseData[
                  i
                ] as any;
                let dataProfileData: T[] = standardReport.results;
                // logger.out.info('dataProfileResponseImpl dataProfileSubject dataProfileData %O', dataProfileData);
                dataProfileSubject.next(dataProfileData);
              }
            } else if (serviceResponse.payload instanceof CubiqTS.SavedOrderEventListImpl) {
              let scratchpadResponse: CubiqTS.SavedOrderEventListImpl =
                serviceResponse.payload as CubiqTS.SavedOrderEventListImpl;
              dataProfileSubject.next(scratchpadResponse.orderDataRequests as any);
              dataProfileSubject.next(scratchpadResponse.fixingOrderRequests as any);
            }

            let responseCode: any = serviceResponse.responseCode;

            if (CubiqTS.ResponseCode.COMPLETE === responseCode) {
              //logger.out.info('dataProfileResponseImpl flow reached end %O', serviceResponse);
              this._isDataProfilePrimed[typeName] = true;
              dataProfileSubject.complete();
              dataProfileSubscription.unsubscribe();
            } else if (CubiqTS.ResponseCode.PARTIAL_COMPLETE === responseCode) {
              //logger.out.info('dataProfileResponseImpl flow in progress: %O', serviceResponse);
            } else if (
              CubiqTS.ResponseCode.BROKER_ERROR === responseCode ||
              CubiqTS.ResponseCode.INVALID_REQUEST === responseCode ||
              CubiqTS.ResponseCode.SERVICE_ERROR === responseCode
            ) {
              logger.out.error("dataProfileResponseImpl error: %O", serviceResponse);
              dataProfileSubject.error(
                Error("dataProfileResponseImpl flow error: " + CubiqTS.ResponseCode[responseCode]),
              );
              dataProfileSubscription.unsubscribe();
            }
          }
        },
        (error) => {
          logger.out.info(error);
          dataProfileSubject.error(error);
          dataProfileSubscription.unsubscribe();
        },
        () => {
          logger.out.info("dataProfileResponseImpl flow completed");
        },
      );

    this.isAuth("CubiqWorker: isAuth primeDataProfile").then(
      () => {
        this.cubiqLogger.processRequest(dataProfileRequest);
        dataProfileRequest.secureToken = this.secureToken;
        this.broker.sendEvent(dataProfileRequest);
      },
      (reason) => {
        logger.out.error("CubiqWorker isAuth primeDataProfile failed");
        logger.out.error(reason);
      },
    );

    return dataProfileSubject.asObservable();
  }

  public primeStaticData<T extends CubiqTS.Encodable | string | string[]>(
    key: string,
  ): Observable<T | T[] | string | string[]> {
    return this.broker.primeStaticData<T>(key);
  }

  public getEncodableObservable<T>(typeName: string): Observable<T> {
    return this.broker.getEncodableObservable<T>(typeName);
  }

  /**
   * isReady
   */
  public isReady(): Promise<void> {
    logger.out.info(`CubiqWorker: this.isReady() %O`, this._ready);
    return this._ready.get<void>();
  }

  public isAuth(requestor?: string): Promise<void> {
    // FIXME: this flow needs to be improved to account for failed initial auth / deauth / reauth flows
    logger.out.info(`CubiqWorker: this.isAuth(${requestor})`);
    return this._auth.get<void>();
  }

  public isOrderDataPrimed(): Promise<void> {
    return this.isDataProfilePrimed(CubiqTS.OrderDataImpl.name);
  }

  public isDataProfilePrimed(typeName: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this._isDataProfilePrimed[typeName]) {
        let checkFn = () => {
          if (this._isDataProfilePrimed[typeName]) {
            resolve();
          } else {
            setTimeout(checkFn, 1000);
          }
        };

        checkFn();
      } else {
        resolve();
      }
    });
  }

  public postServiceRequest(request: CubiqTS.ConcreteEncodable): Promise<any> {
    request["secureToken"] = this.secureToken;

    return new Promise<any>((resolve, reject) => {
      this.isAuth("CubiqWorker.postServiceRequest").then(
        () => {
          this.broker.postServiceRequest(request).then(
            (response) => resolve(response),
            (error) => reject(error),
          );
        },
        (reason) => {
          logger.out.error("CubiqWorker.postServiceRequest isAuth() rejected %O", reason);
          reject(reason);
        },
      );
    });
  }

  public postStandardReportServiceRequest(
    request: RequestEncodable,
  ): Observable<CubiqTS.StandardReport> {
    let subject: Subject<CubiqTS.StandardReport> = new Subject();

    let subscription = this.serviceResponseSubject
      .pipe(
        filter((serviceResponse: CubiqTS.ServiceResponseImpl) => {
          return serviceResponse.messageId === request.messageId;
        }),
      )
      .subscribe({
        next: (serviceResponse: CubiqTS.ServiceResponseImpl) => {
          if (serviceResponse != null && serviceResponse.messageId == request.messageId) {
            let standardReport = serviceResponse.payload as CubiqTS.StandardReport;

            if (standardReport != null) {
              subject.next(standardReport);
            }

            let responseCode: any = serviceResponse.responseCode;

            if (CubiqTS.ResponseCode.COMPLETE === responseCode) {
              subject.complete();
              subscription.unsubscribe();
            } else if (CubiqTS.ResponseCode.PARTIAL_COMPLETE === responseCode) {
              // StandardReport objects yet to arrive
            } else if (
              CubiqTS.ResponseCode.BROKER_ERROR === responseCode ||
              CubiqTS.ResponseCode.INVALID_REQUEST === responseCode ||
              CubiqTS.ResponseCode.SERVICE_ERROR === responseCode
            ) {
              logger.out.error("postStandardReportServiceRequest error: %O", serviceResponse);
              subject.error(
                Error(
                  "postStandardReportServiceRequest flow error: " +
                    CubiqTS.ResponseCode[responseCode],
                ),
              );
              subscription.unsubscribe();
            }
          }
        },
        error: (error) => {
          logger.out.info(error);
          subject.error(error);
          subscription.unsubscribe();
        },
        complete: () => {
          logger.out.info("postStandardReportServiceRequest flow completed");
        },
      });

    this.isAuth("CubiqWorker: isAuth postStandardReportServiceRequest").then(
      () => {
        request.secureToken = this.secureToken;
        logger.out.info(
          "CubiqWorker broker.sendEvent:" +
            request.constructor.name +
            ":time:" +
            Date.now() +
            ":msgId:" +
            request["messageId"],
        );
        this.cubiqLogger.processRequest(request);
        this.broker.sendEvent(request);
      },
      (reason) => {
        logger.out.error("CubiqWorker isAuth primeDataProfile failed");
        logger.out.error(reason);
      },
    );

    return subject.asObservable();
  }

  public requestPasswordReset(
    request: CubiqTS.PasswordChangeRequestImpl,
    processLogin: boolean = true,
  ): Promise<any> {
    if ((!this.isResetRequested || this.resetUserCd == null) && processLogin) return;

    if (this.secureToken != null) {
      request.userCd = this.resetUserCd;
      request.secureToken = this.secureToken;
    }

    return new Promise<void | CubiqTS.Errors | User>((resolve, reject) => {
      this.isReady().then(
        () => {
          let subscription = this.serviceResponseSubject
            .pipe(
              filter((serviceResponse: CubiqTS.ServiceResponseImpl) => {
                return serviceResponse.messageId === request.messageId;
              }),
            )
            .subscribe({
              next: (serviceResponse: CubiqTS.ServiceResponseImpl) => {
                let responseCode: any = serviceResponse.responseCode;
                if (CubiqTS.ResponseCode.COMPLETE === responseCode) {
                  if (
                    serviceResponse.payload != null &&
                    serviceResponse.payload instanceof CubiqTS.TrustToken
                  ) {
                    this.secureToken = serviceResponse.payload;
                    this.tokenSubject.next(this.processToken(this.secureToken));
                    this.isResetRequested = false;
                    this.resetUserCd = null;
                    if (processLogin) {
                      setTimeout(
                        (() => {
                          this.processLogin();
                        }).bind(this),
                        100,
                      );
                    }
                    logger.out.info(`CubiqWorker: requestPasswordReset resolve true`);
                    resolve();
                  } else {
                    logger.out.info(
                      `CubiqWorker: request password response with empty token. Requesting login ...`,
                    );

                    if (processLogin) {
                      let resetUser: User = new User(this.resetUserCd, request.newPassword);

                      this.isResetRequested = false;
                      this.resetUserCd = null;
                      this.secureToken = null;
                      resolve(resetUser);
                    }
                  }
                  subscription.unsubscribe();
                } else if (
                  CubiqTS.ResponseCode.INVALID_REQUEST === responseCode ||
                  CubiqTS.ResponseCode.SERVICE_ERROR === responseCode ||
                  CubiqTS.ResponseCode.BROKER_ERROR === responseCode
                ) {
                  logger.out.info(`CubiqWorker: requestPasswordReset resolve false`);
                  subscription.unsubscribe();
                  resolve(serviceResponse.errorList);
                }
              },
              error: (error) => {
                logger.out.error(`CubiqWorker: requestPasswordReset()`);
                this._auth.reject(error);
                reject(error);
              },
              complete: () => {
                logger.out.info("logonRequest flow completed");
              },
            });

          this.broker.sendEvent(request);
        },
        (reason) => {
          logger.out.error(`CubiqWorker: requestPasswordReset() `, reason);
        },
      );
    });
  }

  public getUserName(): string {
    return this.secureToken.userName;
  }

  public getUserId(): string {
    return this.broker.getUserId();
  }

  public getLoggedInUser(): CubiqTS.UserImpl {
    return this.broker.getLoggedInUser();
  }

  public getConnectionStatus(): ConnectionStatus {
    return this.broker.getConnectionStatus();
  }

  public getSystemName(): string {
    return this.broker.getSystemName();
  }

  public getIsResetRequested(): boolean {
    return this.isResetRequested;
  }

  public disconnect(): void {
    this.broker.disconnect();
    this.secureToken = null;
  }

  public disconectOnLogout(): void {
    logger.out.info(`${this.constructor.name}.disconectLogout()`);
    this.broker.disconnectOnLogout();
    this.secureToken = null;
    // TODO
  }

  private processToken(tk: CubiqTS.TrustToken): SessionStoreData {
    return new SessionStoreData(tk.id, tk.signature);
  }

  public refreshAppRequest(storeData: SessionStoreData): void {}
}
